// Gate readiness — Day 15 / 30 / 45 observed checks extracted from the Gates
// page so Command, The Bridge, and Gates all read the same recorded state.
// Pure functions. Founder checkboxes on gateReviews are included when present.

import { weightOf } from './derive';
import { orgGateOutcomes, orgPrimaryDealEnabled } from './orgPlan';

function bucket(check) {
  if (check.ok) return 'likelyPass';
  if (check.assessable === false) return 'unknown';
  if (check.hypothesis) return 'atRisk';
  if (check.progress) return 'atRisk';
  return 'missing';
}

function pack(checks, smallestActions) {
  const likelyPass = [];
  const atRisk = [];
  const missing = [];
  const unknown = [];
  for (const c of checks) {
    const row = {
      id: c.id || '',
      label: c.label,
      detail: c.detail || '',
      ok: !!c.ok,
      hypothesis: !!c.hypothesis,
      assessable: c.assessable !== false,
    };
    const b = bucket(c);
    if (b === 'likelyPass') likelyPass.push(row);
    else if (b === 'atRisk') atRisk.push(row);
    else if (b === 'unknown') unknown.push(row);
    else missing.push(row);
  }
  return { likelyPass, atRisk, missing, unknown, smallestActions: smallestActions.slice(0, 3) };
}

function progressed(n) {
  return Number(n) > 0;
}

/** Consulting / Hybrid Package Wedge gate checks (no Dubai/Saudi/D001). */
export function consultingDay15Checks(state, derived) {
  const { funnel, runway, time } = derived;
  const c = funnel.counts;
  const t = funnel.targets || {};
  const outcomes = orgGateOutcomes(state).filter((o) => o.gateDay === 15);
  const universeTarget = t.universe || 20;
  const burnUnknown = runway.cashUnknown || runway.burnUnknown || runway.baselineKind === 'Unknown';
  const timeUnknown = !time.hasMinimumHours;
  const checks = [
    {
      id: 'week1',
      ok: c.tierA >= 10,
      progress: progressed(c.tierA),
      hypothesis: true,
      label: 'Are Week-1 priorities confirmed (≤10–15 Tier A)?',
      detail: c.tierA >= 10 ? `${c.tierA} Tier-A accounts` : `${c.tierA} Tier-A — below Week-1 bar`,
      action: 'Confirm Week-1 Tier-A priorities across org markets (MENA + NA–EU).',
    },
    {
      id: 'touches',
      ok: c.contacted >= 10,
      progress: progressed(c.contacted),
      hypothesis: true,
      label: 'Have ≥10 personalized touches been sent?',
      detail: `${c.contacted} accounts reached Contacted`,
      action: 'Ship personalized Play 1 touches on the Week-1 set.',
    },
    {
      id: 'discovery',
      ok: c.discovery >= 1,
      progress: progressed(c.discovery),
      hypothesis: true,
      label: 'Is ≥1 discovery scheduled or held?',
      detail: `${c.discovery} accounts at Discovery`,
      action: 'Book or complete one discovery conversation on the package wedge.',
    },
    {
      id: 'universe',
      ok: c.universe >= Math.min(universeTarget, 10),
      progress: progressed(c.universe),
      hypothesis: true,
      label: 'Is the Top-20 / Week-1 universe loaded?',
      detail: `${c.universe} accounts against operating hypothesis (${universeTarget})`,
      action: 'Load Week-1 accounts into Pipeline (manual/CSV is first-class).',
    },
    {
      id: 'burn',
      ok: !burnUnknown || runway.baselineKind === 'Unknown',
      progress: false,
      hypothesis: burnUnknown,
      assessable: true,
      label: 'Is runway posture acceptable for consulting lean?',
      detail: burnUnknown ? 'Runway Unknown — proceed is allowed for consulting lean GTM.' : 'Runway baseline recorded.',
      action: 'Keep consulting lean runway mode (Unknown — proceed is fine).',
      optional: true,
    },
    {
      id: 'founder-time',
      ok: !timeUnknown && time.internalShare !== null && time.internalShare < 40,
      progress: !timeUnknown,
      assessable: !timeUnknown,
      hypothesis: timeUnknown,
      label: 'Is founder time predominantly external?',
      detail: timeUnknown ? 'Not assessable — founder-time baseline not recorded.' : `${time.internalShare}% internal`,
      action: 'Move founder hours from administration to external counterparties.',
    },
  ];
  for (const o of outcomes) {
    checks.push({
      id: o.id,
      ok: false,
      progress: false,
      hypothesis: true,
      label: o.requiredOutcome,
      detail: `Plan gate outcome (${o.epicIds?.join(', ') || 'epics'})`,
      action: `Advance: ${o.requiredOutcome}`,
    });
  }
  // Deduplicate by label
  const seen = new Set();
  return checks.filter((ch) => {
    const k = ch.label;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

export function consultingDay30Checks(state, derived) {
  const { funnel } = derived;
  const c = funnel.counts;
  const t = funnel.targets || {};
  const outcomes = orgGateOutcomes(state).filter((o) => o.gateDay === 30);
  const checks = [
    {
      id: 'discoveries',
      ok: c.discovery >= (t.conversations || 3),
      progress: progressed(c.discovery),
      label: '≥3 discoveries completed',
      detail: `${c.discovery} discoveries`,
      action: 'Convert engaged accounts into discovery conversations.',
    },
    {
      id: 'proposal',
      ok: (c.proposals || 0) >= (t.proposals || 1),
      progress: progressed(c.proposals),
      label: '≥1 proposal issued (Pkg 1–4)',
      detail: `${c.proposals || 0} proposal(s)`,
      action: 'Issue a package proposal with a commercial next step.',
    },
    {
      id: 'qualified',
      ok: c.qualified >= 1,
      progress: progressed(c.qualified),
      label: 'At least one qualified package opportunity',
      detail: `${c.qualified} qualified`,
      action: 'Qualify a discovery into a package opportunity.',
    },
  ];
  for (const o of outcomes) {
    checks.push({
      id: o.id,
      ok: false,
      progress: false,
      hypothesis: true,
      label: o.requiredOutcome,
      detail: `Plan gate outcome`,
      action: `Advance: ${o.requiredOutcome}`,
    });
  }
  const seen = new Set();
  return checks.filter((ch) => {
    if (seen.has(ch.label)) return false;
    seen.add(ch.label);
    return true;
  });
}

export function consultingDay45Checks(state, derived) {
  const { funnel } = derived;
  const c = funnel.counts;
  const outcomes = orgGateOutcomes(state).filter((o) => o.gateDay === 45);
  const checks = [
    {
      id: 'sow',
      ok: (c.sows || 0) >= 1 || (c.proposals || 0) >= 1,
      progress: progressed(c.sows) || progressed(c.proposals),
      label: 'SOW path open or written learnings + Play decision',
      detail: `${c.sows || 0} SOW path(s), ${c.proposals || 0} proposal(s)`,
      action: 'Close a SOW / deposit or write the Day-45 Play decision.',
    },
  ];
  for (const o of outcomes) {
    checks.push({
      id: o.id,
      ok: false,
      progress: false,
      hypothesis: true,
      label: o.requiredOutcome,
      detail: `Plan gate outcome`,
      action: `Advance: ${o.requiredOutcome}`,
    });
  }
  const seen = new Set();
  return checks.filter((ch) => {
    if (seen.has(ch.label)) return false;
    seen.add(ch.label);
    return true;
  });
}

export function day15Checks(state, derived) {
  const { d001, saudi, runway, time, funnel } = derived;
  const c = funnel.counts;
  const t = funnel.targets || {};
  const universeTarget = t.universe || 75;
  const universeOk = c.universe >= universeTarget * 0.5;
  const partnerCount = d001.partners.length;
  const burnUnknown = runway.cashUnknown || runway.burnUnknown || runway.baselineKind === 'Unknown';
  const burnMoved = !burnUnknown && runway.baseline !== null && runway.burn !== null && runway.baseline > runway.burn;
  const timeUnknown = !time.hasMinimumHours;
  return [
    {
      id: 'universe',
      ok: universeOk,
      progress: progressed(c.universe),
      hypothesis: true,
      label: 'Is the target universe large enough?',
      detail: universeOk
        ? `${c.universe} accounts against a ${universeTarget} operating hypothesis`
        : `${c.universe} accounts — below operating hypothesis (${universeTarget})`,
      action: 'Build the Dubai target universe to the operating hypothesis.',
    },
    {
      id: 'tiera',
      ok: c.tierA >= 10,
      progress: progressed(c.tierA),
      hypothesis: true,
      label: 'Are at least 10–15 strong targets actively being worked?',
      detail: c.tierA >= 10 ? `${c.tierA} Tier-A accounts` : `${c.tierA} Tier-A accounts — below operating hypothesis`,
      action: 'Concentrate founder time on ten Tier-A Dubai accounts.',
    },
    {
      id: 'discovery',
      ok: c.discovery >= 5,
      progress: progressed(c.discovery),
      hypothesis: true,
      label: 'Are substantive conversations occurring?',
      detail: c.discovery >= 5
        ? `${c.discovery} accounts have reached discovery`
        : `${c.discovery} accounts have reached discovery — below operating hypothesis`,
      action: 'Convert engaged accounts into discovery conversations.',
    },
    {
      id: 'qualified',
      ok: c.qualified >= 1,
      progress: progressed(c.qualified),
      label: 'Has at least one real opportunity emerged?',
      detail: `${c.qualified} qualified opportunity(ies)`,
      action: 'Convert a discovery into a qualified opportunity with a named next step.',
    },
    {
      id: 'asset',
      ok: !!state.d001?.asset?.namedAssetExists || c.assetsIdentified > 0,
      progress: progressed(c.assetsIdentified),
      label: 'Has a concrete asset surfaced?',
      detail: c.assetsIdentified ? `${c.assetsIdentified} asset candidate(s)` : 'No named asset yet — not required at Day 15',
      action: 'Get one sponsor to name a specific asset and share information about it.',
      optional: true,
    },
    {
      id: 'partner',
      ok: partnerCount > 0 && d001.criticalUnknown.length < 6,
      progress: partnerCount > 0,
      assessable: partnerCount > 0,
      label: 'Is the partner-led execution route credible?',
      detail: partnerCount === 0
        ? 'Not assessable—no partner architecture candidate'
        : `${partnerCount} partner(s), ${d001.criticalUnknown.length} critical unknown(s)`,
      action: 'Name a licensed execution partner and close Unknowns on critical activities.',
    },
    {
      id: 'saudi-pain',
      ok: saudi.totals.painConfirmed > 0,
      progress: progressed(saudi.totals.meetings),
      label: 'Saudi: is enterprise pain real?',
      detail: `${saudi.totals.painConfirmed} pain-confirmed`,
      action: 'Run one Saudi discovery that confirms a real problem, not a meeting.',
    },
    {
      id: 'burn',
      ok: burnMoved,
      progress: false,
      hypothesis: burnUnknown,
      assessable: !burnUnknown,
      label: 'Has burn decreased?',
      detail: burnUnknown
        ? 'Not assessable — starting burn baseline not recorded.'
        : `${runway.baseline} → ${runway.burn}`,
      action: 'Cut spend that is not buying evidence. Burn reset is a Day-15 gate item.',
    },
    {
      id: 'founder-time',
      ok: !timeUnknown && time.internalShare !== null && time.internalShare < 40,
      progress: !timeUnknown,
      assessable: !timeUnknown,
      hypothesis: timeUnknown,
      label: 'Is founder time predominantly external?',
      detail: timeUnknown ? 'Not assessable — founder-time baseline not recorded.' : `${time.internalShare}% internal`,
      action: 'Move founder hours from administration to external counterparties.',
    },
  ];
}

export function day30Checks(state, derived) {
  const { d001, saudi, infra, evidence, funnel } = derived;
  const c = funnel.counts;
  const t = funnel.targets || {};
  const g = state.gateReviews?.g30 || {};

  const density = [
    {
      id: 'qualified-density',
      ok: c.qualified >= (t.qualifiedOpportunities || 4),
      progress: progressed(c.qualified),
      label: '4–6 qualified opportunities',
      detail: `${c.qualified} recorded`,
      action: 'Convert discovery into qualified opportunities before concentrating.',
    },
    {
      id: 'assets',
      ok: c.assetsIdentified >= (t.assets || 2),
      progress: progressed(c.assetsIdentified),
      label: '2–3 credible assets',
      detail: `${c.assetsIdentified} recorded`,
      action: 'Get two sponsors to share named-asset information.',
    },
    {
      id: 'candidates',
      ok: c.candidates >= (t.candidates || 2),
      progress: progressed(c.candidates),
      label: 'Ideally 2 D001 candidates',
      detail: `${c.candidates} recorded`,
      action: 'Screen identified assets into a second D001 candidate.',
    },
    {
      id: 'primary',
      ok: c.primary >= 1,
      progress: progressed(c.candidates),
      label: 'One Primary D001 if evidence supports selection',
      detail: funnel.primary ? funnel.primary.name : 'none designated',
      action: 'Compare candidates and designate a Primary with a named backup.',
    },
    {
      id: 'backup',
      ok: c.backups >= 1,
      progress: progressed(c.candidates),
      label: 'At least one backup path',
      detail: `${c.backups} backup(s)`,
      action: 'Develop one credible backup behind Primary D001.',
    },
  ];

  const dubai = [
    {
      id: 'named-asset',
      ok: !!state.d001?.asset?.namedAssetExists && !!state.d001?.asset?.titleInfoAvailable,
      progress: !!state.d001?.asset?.namedAssetExists,
      label: 'Real asset + documents',
      action: 'Close the D001 asset gap: title information from the sponsor.',
    },
    {
      id: 'paid-proposal',
      ok: (state.infra?.opportunities || []).some((o) => o.paidPoc) || !!g.paidProposal,
      progress: false,
      label: 'Paid readiness / implementation proposal',
      action: 'Issue a priced readiness or implementation proposal a buyer asked for.',
    },
    {
      id: 'dubai-e4',
      ok: (evidence.byMarket.Dubai?.E4 || 0) + (evidence.byMarket.Dubai?.E5 || 0) > 0,
      progress: (evidence.byMarket.Dubai?.E3 || 0) > 0,
      label: 'Sponsor at E4 or better',
      action: 'Advance the strongest Dubai opportunity to a commercial commitment (E4).',
    },
    {
      id: 'partner-pilot',
      ok: d001.partnerReady.length > 0,
      progress: d001.partners.length > 0,
      label: 'Partner at Pilot Ready',
      action: 'Move a licensed partner to Pilot Ready on the D001 architecture.',
    },
    {
      id: 'capital-signal',
      ok: (state.d001?.capital?.conversations || []).some((x) => x.acceptanceSignal),
      progress: (state.d001?.capital?.conversations || []).length > 0,
      label: 'D001 capital signal',
      action: 'Secure a D001-specific acceptance signal from a capital channel.',
    },
  ];

  const saudiTests = [
    {
      id: 'saudi-budget',
      ok: saudi.totals.budget > 0,
      progress: progressed(saudi.totals.proposals),
      label: 'Saudi budget identified',
      action: 'Do not expand Saudi until a named budget exists.',
    },
    {
      id: 'saudi-poc',
      ok: saudi.totals.proposals > 0 || saudi.totals.paid > 0,
      progress: progressed(saudi.totals.pocs),
      label: 'Saudi POC or proposal',
      action: 'A Saudi POC is evidence. More meetings are not.',
    },
    {
      id: 'saudi-usecase',
      ok: saudi.signals.find((s) => s.key === 'useCase')?.value === 'strong',
      progress: saudi.signals.find((s) => s.key === 'useCase')?.value === 'weak',
      label: 'Real contribution / use case',
      action: 'Confirm a real developer / Contribution use case before allocating more hours.',
    },
  ];

  const infraTests = [
    {
      id: 'infra-proposal',
      ok: infra.live.some((o) => Number(String(o.proposalValue).replace(/[^0-9.]/g, '')) > 0 && weightOf(o.evidenceLevel) >= 4),
      progress: infra.proposals > 0,
      label: 'Serious priced infrastructure proposal',
      action: infra.closest
        ? `${infra.closest.account || 'Closest buyer'}: issue a priced proposal.`
        : 'Name the infrastructure buyer closest to a paid POC.',
    },
  ];

  return [...density, ...dubai, ...saudiTests, ...infraTests];
}

export function day45Checks(state, derived) {
  const { evidence, saudi, funnel } = derived;
  const c = funnel.counts;
  const g = state.gateReviews?.g45 || {};
  const dubaiE = (evidence.byMarket.Dubai?.E4 || 0) + (evidence.byMarket.Dubai?.E5 || 0);
  const infraE = (evidence.byMarket.Infrastructure?.E4 || 0) + (evidence.byMarket.Infrastructure?.E5 || 0);

  return [
    {
      id: 'dubai-e4e5',
      ok: dubaiE > 0,
      progress: (evidence.byMarket.Dubai?.E3 || 0) > 0,
      label: 'Primary D001 reached E4 or E5',
      detail: `${dubaiE} Dubai E4/E5`,
      action: 'The sprint score is E4/E5. Everything else is activity.',
    },
    {
      id: 'paid-readiness',
      ok: !!g.paidReadiness,
      progress: false,
      label: 'Paid readiness, signed mandate or implementation agreement',
      action: 'Convert the commercial conversation into a paid or signed path.',
    },
    {
      id: 'funnel-volume',
      ok: c.universe >= 40,
      progress: progressed(c.universe),
      label: 'Can generate relevant targets at volume',
      detail: `${c.universe} accounts`,
      action: 'The funnel must be able to replace a dead Primary.',
    },
    {
      id: 'funnel-convert',
      ok: c.qualified >= 3,
      progress: progressed(c.qualified),
      label: 'Can convert conversations into real opportunities',
      detail: `${c.qualified} qualified`,
      action: 'Qualification conversion is the machine, not more names.',
    },
    {
      id: 'saudi-earned',
      ok: saudi.count >= 2,
      progress: saudi.count === 1,
      label: 'Saudi has earned more than validation hours',
      detail: `${saudi.count} / 4 strong signals`,
      action: 'Saudi stays validation until two strong signals exist.',
    },
    {
      id: 'infra-e4e5',
      ok: infraE > 0,
      progress: derived.infra.proposals > 0,
      label: 'Infrastructure produced E4/E5',
      detail: `${infraE} Infrastructure E4/E5`,
      action: 'The cash engine is not an engine until a buyer pays or commits.',
    },
  ];
}

export function deriveGateReadiness(state, derived, gateDay) {
  const day = Number(gateDay || derived.sprint?.nextGate?.day || 15);
  const consulting = !orgPrimaryDealEnabled(state) && orgGateOutcomes(state).length > 0;
  let checks;
  if (consulting) {
    checks =
      day >= 45
        ? consultingDay45Checks(state, derived)
        : day >= 30
          ? consultingDay30Checks(state, derived)
          : consultingDay15Checks(state, derived);
  } else {
    checks = day >= 45 ? day45Checks(state, derived) : day >= 30 ? day30Checks(state, derived) : day15Checks(state, derived);
  }
  const required = checks.filter((c) => !c.optional);
  const smallestActions = required.filter((c) => !c.ok).map((c) => c.action).filter(Boolean);
  const packed = pack(required, smallestActions);
  const unknownLabels = packed.unknown.map((u) => u.detail || u.label);
  const largestGap = packed.missing[0] || packed.atRisk[0] || null;
  const preSprint = derived.sprint?.phase === 'pre';
  const initializationIncomplete = preSprint && (
    (state.accounts || []).length === 0 ||
    derived.runway.cashUnknown ||
    derived.runway.burnUnknown ||
    !derived.time.hasMinimumHours
  );
  const assessable = packed.likelyPass.length + packed.atRisk.length + packed.missing.length;
  const status = preSprint
    ? 'NOT ASSESSABLE'
    : assessable === 0
      ? 'NOT ASSESSABLE'
      : packed.missing.length
        ? 'OFF TRACK'
        : packed.atRisk.length
          ? 'AT RISK'
          : 'ON TRACK';
  const nextMove = preSprint && (state.accounts || []).length === 0
    ? consulting
      ? 'Initialization next move: Load Week-1 accounts (manual / CSV).'
      : 'Initialization next move: Import the existing Dubai Top 50.'
    : smallestActions[0] || '';
  return {
    day,
    label: `DAY ${day} READINESS`,
    ...packed,
    checks,
    compact: {
      ready: packed.likelyPass.length,
      atRisk: packed.atRisk.length,
      unknown: packed.unknown.length,
      missing: packed.missing.length,
      largestGap: largestGap ? largestGap.label : '',
      unknownInputs: unknownLabels,
      nextMove,
      status,
      initializationIncomplete,
      reason: preSprint
        ? 'Initialization incomplete'
        : status === 'NOT ASSESSABLE'
          ? 'Required gate inputs are not assessable'
          : '',
    },
  };
}
